// El archivo de papeles.
//
// Aquí no se calcula nada nuevo: se ordena y se filtra lo que el servidor ya
// devolvió. Todo lo que sea una cuenta —el neto de un pago, el total de una
// venta— viene de donde se guardó, no se rehace. Rehacerla sería una segunda
// cuenta que puede diferir de la del papel original, que es justo lo que un
// archivo no puede permitirse.

export const CLASES = {
  venta:      { ico: '🧾', nombre: 'Remisión de venta',    verbo: 'Se le vendió a' },
  finca:      { ico: '💵', nombre: 'Comprobante — leche',  verbo: 'Se le pagó a' },
  trabajador: { ico: '💵', nombre: 'Comprobante — nómina', verbo: 'Se le pagó a' },
};

const texto = (x) => String(x == null ? '' : x).toLowerCase();

// Busca por a quién, por cédula y por el número del papel.
//
// Por el número también porque es lo que trae escrito el papel que llega de
// vuelta: el cliente no dice «la venta de Supermercado La 14», dice «la 47».
// «REM-0003» y «3» son el mismo papel: quien busca teclea lo que ve.
//
// Las dos partes tienen que ser NÚMEROS de verdad. Sin esa guarda,
// `parseInt('osnaider')` da NaN, `String(NaN) === String(NaN)` es verdadero, y
// buscar un nombre devolvía todos los papeles sin consecutivo.
function mismoNumero(busca, consecutivo) {
  const a = String(busca).replace(/\D/g, '');
  const b = String(consecutivo || '').replace(/\D/g, '');
  if (!a || !b) return false;
  return parseInt(a, 10) === parseInt(b, 10);
}

export function filtrar(papeles, q) {
  const b = texto(q).trim();
  if (b.length < 1) return papeles || [];
  return (papeles || []).filter(p =>
    texto(p.quien).includes(b) ||
    texto(p.documento).includes(b) ||
    // EL CONSECUTIVO. Es el número que sale impreso y el que trae en la mano
    // quien viene a buscar un papel. Se acepta «REM-0003» y «3» a secas: el
    // que busca teclea lo que ve, y exigirle el formato exacto es cómo se
    // consigue que deje de usar el buscador.
    texto(p.consecutivo).includes(b) ||
    mismoNumero(b, p.consecutivo) ||
    String(p.id) === b ||
    texto(CLASES[p.tipo] && CLASES[p.tipo].nombre).includes(b));
}

// Cuántos de cada clase y cuánto suman. Se cuentan por separado porque no se
// pueden sumar entre sí: una venta es plata que ENTRA y un comprobante es plata
// que SALE. Un total único de los tres no significa nada.
export function resumen(papeles) {
  const r = { venta: { n: 0, valor: 0 }, finca: { n: 0, valor: 0 },
              trabajador: { n: 0, valor: 0 }, total: (papeles || []).length };
  for (const p of papeles || []) {
    if (!r[p.tipo]) continue;
    r[p.tipo].n += 1;
    r[p.tipo].valor += Number(p.valor) || 0;
  }
  return r;
}

// Arma la remisión para volver a imprimirla, con las líneas de esa venta.
export function remisionDe(papel, lineas) {
  return {
    serie: 'REM',
    numero: papel.id, fecha: papel.fecha, total: papel.valor,
    forma_pago: papel.forma_pago,
    // La copia tiene que salir idéntica al original, y el descuento es parte
    // del original: sin él los renglones no cuadrarían con el total.
    descuento: papel.descuentos, descuento_kilos: papel.desc_kilos,
    cliente: papel.quien, documento: papel.documento,
    ciudad: papel.ciudad, direccion: papel.direccion,
    lineas: (lineas || [])
      .filter(l => l.venta_id === papel.id)
      .map(l => ({
        producto: l.producto, lote: l.lote || null, unidad: l.unidad,
        kilos: l.kilos > 0 ? l.kilos : l.piezas, precio: l.precio,
      })),
  };
}

// Y el comprobante de egreso, de finca o de trabajador.
//
// El detalle de los renglones solo se arma cuando el pago guardó de dónde salen
// las cifras. Inventar un desglose que cuadre con el neto sería escribir en un
// papel una cuenta que nadie hizo.
export function comprobanteDe(papel) {
  const esFinca = papel.tipo === 'finca';
  const renglones = [];
  if (!esFinca && papel.bruto > 0) {
    renglones.push({ texto: 'Trabajo del periodo', monto: papel.bruto });
    if (papel.descuentos > 0) {
      renglones.push({ texto: 'Menos descuentos', monto: papel.descuentos, signo: '-' });
    }
  }
  return {
    serie: esFinca ? 'PAG' : 'NOM',
    numero: papel.id, fecha: papel.fecha, monto: papel.valor,
    aQuien: papel.quien, documento: papel.documento,
    concepto: esFinca
      ? `Leche de ${papel.tipo_leche === 'bufala' ? 'búfala' : 'vaca'}` +
        (papel.nota ? ` · ${papel.nota}` : '')
      : (papel.nota || 'Trabajo del periodo'),
    forma: 'efectivo',
    renglones,
  };
}
